package pl.nickers.match3.ecs

/**
 * Lightweight piece-definition queries used by the dispatch migration
 * (Phase 4). Systems use these helpers to look up activation triggers,
 * effect kinds, and modifiers from the active [PieceSet] without
 * touching legacy marker components.
 *
 * All helpers are null-safe and fall back to "nothing matches" semantics
 * for entities without a [PieceIdComponent] — this keeps legacy entities
 * (e.g. snapshots saved before Phase 2) from crashing the dispatcher.
 */

/** The canonical piece id attached to [entity], or null if absent. */
fun World.pieceIdOf(entity: Int): String? =
    getComponent(entity, PieceIdComponent::class)?.pieceId

/** The piece definition for [entity], or null if missing/unknown. */
fun World.pieceDefOf(entity: Int, pieceSet: PieceSet): PieceDefinition? {
    val pieceId = pieceIdOf(entity) ?: return null
    return pieceSet.pieces[pieceId]
}

/** The piece family for [entity], or null if missing/unknown. */
fun World.familyOf(entity: Int, pieceSet: PieceSet): Family? =
    pieceDefOf(entity, pieceSet)?.family

/** True iff [entity] belongs to the given piece [family]. Null-safe. */
fun World.isFamily(entity: Int, pieceSet: PieceSet, family: Family): Boolean =
    familyOf(entity, pieceSet) == family

/**
 * Beam direction for a laser entity, read from its [PieceParams.Laser]. Returns
 * null if [entity] is not a laser (or has no piece definition).
 */
fun World.laserDirectionOf(entity: Int, pieceSet: PieceSet): LaserDirection? {
    val def = pieceDefOf(entity, pieceSet) ?: return null
    return (def.params as? PieceParams.Laser)?.direction
}

/** True iff [entity]'s piece definition declares the given activation trigger. */
fun World.hasActivation(entity: Int, pieceSet: PieceSet, activation: Activation): Boolean =
    pieceDefOf(entity, pieceSet)?.activations?.contains(activation) == true

/** True iff [entity]'s piece definition declares the given effect modifier. */
fun World.hasModifier(entity: Int, pieceSet: PieceSet, modifier: Modifier): Boolean =
    pieceDefOf(entity, pieceSet)?.modifiers?.contains(modifier) == true

/** The on-activate effect step for [entity], or null if none defined. */
fun World.onActivateOf(entity: Int, pieceSet: PieceSet): EffectStep? =
    pieceDefOf(entity, pieceSet)?.onActivate

/** The on-destroyed effect step for [entity], or null if none defined. */
fun World.onDestroyedOf(entity: Int, pieceSet: PieceSet): EffectStep? =
    pieceDefOf(entity, pieceSet)?.onDestroyed
